<template>
  <div class="contact-crm-info">
    <!-- Header -->
    <div class="crm-section-header">
      <span class="crm-section-title">
        <fluent-icon icon="person-chat" size="14" />
        {{ $t('CRM_CONFIGS.INFO.TITLE') }}
      </span>
      <div class="crm-section-actions">
        <woot-button
          size="tiny"
          variant="smooth"
          color-scheme="primary"
          icon="add"
          @click="openNoteModal"
        >
          {{ $t('CRM_CONFIGS.NOTES.ADD_BTN') }}
        </woot-button>
        <woot-button
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="edit"
          @click="$emit('edit-crm')"
        >
          Sửa
        </woot-button>
        <woot-button
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="dismiss"
          @click="unlinkContact"
        >
          {{ $t('CRM_CONFIGS.BILL_HISTORY.UNLINK_BTN') }}
        </woot-button>
      </div>
    </div>

    <!-- Contact Info -->
    <div class="crm-contact-card">
      <div class="crm-contact-row">
        <span class="crm-label">{{ $t('CRM_CONFIGS.INFO.GUEST_ID') }}</span>
        <span class="crm-value mono">{{ displayedCrmContact.maKhach }}</span>
      </div>
      <div class="crm-contact-row">
        <span class="crm-label">{{ $t('CRM_CONFIGS.INFO.PHONE') }}</span>
        <span class="crm-value">{{
          displayedCrmContact.dienThoai || '—'
        }}</span>
      </div>
      <div v-if="displayedCrmContact.email" class="crm-contact-row">
        <span class="crm-label">{{ $t('CRM_CONFIGS.INFO.EMAIL') }}</span>
        <span class="crm-value truncate">{{ displayedCrmContact.email }}</span>
      </div>
    </div>

    <!-- Tabs -->
    <div class="crm-tabs">
      <button
        v-for="tab in tabItems"
        :key="tab.id"
        class="crm-tab"
        :class="{ 'crm-tab--active': activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Create Note Modal -->
    <modal :show.sync="showCreateNoteModal" :on-close="closeNoteModal">
      <div class="column content-box">
        <woot-modal-header
          :header-title="$t('CRM_CONFIGS.NOTES.ADD_TITLE')"
          :header-content="$t('CRM_CONFIGS.NOTES.ADD_DESCRIPTION')"
        />
        <form class="row" @submit.prevent="submitCreateNote">
          <div class="medium-12 columns">
            <label>
              {{ $t('CRM_CONFIGS.NOTES.LABEL_TYPE') }}
              <select v-model="newNote.noteType">
                <option :value="0">
                  {{ $t('CRM_CONFIGS.NOTES.TYPE_INTERNAL') }}
                </option>
                <option :value="1">
                  {{ $t('CRM_CONFIGS.NOTES.TYPE_POS') }}
                </option>
                <option :value="2">
                  {{ $t('CRM_CONFIGS.NOTES.TYPE_PMS') }}
                </option>
                <option :value="3">
                  {{ $t('CRM_CONFIGS.NOTES.TYPE_POS_INTERNAL') }}
                </option>
              </select>
            </label>
          </div>
          <div class="medium-12 columns">
            <label>
              {{ $t('CRM_CONFIGS.NOTES.LABEL_MESSAGE') }}
              <textarea
                ref="noteTextArea"
                v-model="newNote.message"
                rows="4"
                :placeholder="$t('CRM_CONFIGS.NOTES.PLACEHOLDER_MESSAGE')"
              />
            </label>
          </div>
          <div class="medium-12 columns modal-footer">
            <woot-button
              type="button"
              variant="clear"
              color-scheme="secondary"
              @click.prevent="closeNoteModal"
            >
              {{ $t('CRM_CONFIGS.NOTES.CANCEL') }}
            </woot-button>
            <woot-button type="submit" :is-loading="isCreatingNote">
              {{ $t('CRM_CONFIGS.NOTES.SUBMIT') }}
            </woot-button>
          </div>
        </form>
      </div>
    </modal>

    <!-- Tab Content -->
    <div class="crm-tab-content">
      <!-- Loading -->
      <div v-if="loadingStates[activeTab]" class="crm-state-center">
        <spinner size="small" />
      </div>

      <!-- Error -->
      <div v-else-if="errorStates[activeTab]" class="crm-state-center">
        <fluent-icon icon="error-circle" size="20" class="text-red-400" />
        <span class="crm-state-text">{{
          $t(`CRM_CONFIGS.${tabErrorKey}.FETCH_ERROR`)
        }}</span>
      </div>

      <!-- Empty -->
      <div v-else-if="isTabEmpty(activeTab)" class="crm-state-center">
        <fluent-icon
          :icon="getTabIcon(activeTab)"
          size="20"
          class="crm-empty-icon"
        />
        <span class="crm-state-text">{{ emptyMessage }}</span>
      </div>

      <!-- Bills -->
      <div v-else-if="activeTab === 'bills'" class="crm-list">
        <div v-for="bill in bills" :key="bill.id" class="crm-item">
          <div class="crm-item-header">
            <div class="crm-item-title">{{ bill.branch }}</div>
            <div class="crm-item-amount">
              {{ formatCurrency(bill.totalAmount) }}
            </div>
          </div>
          <div class="crm-item-meta">
            <span>#{{ bill.code }}</span>
            <span>{{ formatDate(bill.dateSpent) }}</span>
          </div>
          <div
            v-if="bill.products && bill.products.length"
            class="crm-item-products"
          >
            <span
              v-for="(product, idx) in bill.products.slice(0, 3)"
              :key="idx"
              class="crm-product-chip"
            >
              {{ formatBillProductName(product.productName) }}
            </span>
            <span v-if="bill.products.length > 3" class="crm-product-more">
              +{{ bill.products.length - 3 }}
            </span>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div v-else-if="activeTab === 'notes'" class="crm-list">
        <div v-for="note in notes" :key="note.id" class="crm-item">
          <div class="crm-item-header">
            <span :class="['crm-badge', noteTypeColor(note.noteType)]">
              {{ noteTypeString(note.noteType) }}
            </span>
            <span class="crm-item-date">{{ formatDate(note.ngayTao) }}</span>
          </div>
          <p class="crm-note-body">{{ note.message }}</p>
          <div class="crm-note-author">@{{ note.username || 'system' }}</div>
        </div>
      </div>

      <!-- Products (Statistics) -->
      <div v-else-if="activeTab === 'products'" class="crm-list">
        <div
          v-for="product in productStats"
          :key="product.id"
          class="crm-product-stat"
        >
          <div class="crm-product-stat-header">
            <span class="crm-product-name">{{ product.name }}</span>
            <span class="crm-product-qty">x{{ product.quantity }}</span>
          </div>
          <div class="crm-progress-bar">
            <div
              class="crm-progress-fill"
              :style="{ width: getProductWeight(product.quantity) + '%' }"
            />
          </div>
          <div class="crm-product-footer">
            <span>{{ formatCurrency(product.totalPrice) }}</span>
            <span>{{ formatDate(product.lastBought, true) }}</span>
          </div>
        </div>
      </div>

      <!-- Vouchers -->
      <div v-else-if="activeTab === 'vouchers'" class="crm-list">
        <div v-for="item in vouchers" :key="item.id" class="crm-item">
          <div class="crm-item-header">
            <span class="crm-voucher-code">{{ item.code }}</span>
            <span
              :class="[
                'crm-badge',
                item.isExpired ? 'crm-badge--red' : 'crm-badge--green',
              ]"
            >
              {{
                item.isExpired
                  ? $t('CRM_CONFIGS.VOUCHERS.EXPIRED')
                  : $t('CRM_CONFIGS.VOUCHERS.VALID')
              }}
            </span>
          </div>
          <div class="crm-item-title mt-1">
            {{ item.voucher && item.voucher.description }}
          </div>
          <div class="crm-item-meta">
            <span>{{ formatVoucherValue(item) }}</span>
            <span>{{ formatDate(item.expiredTime, true) }}</span>
          </div>
        </div>
      </div>

      <!-- Bookings (Lưu trú) -->
      <div v-else-if="activeTab === 'bookings'" class="crm-list">
        <div v-for="booking in bookings" :key="booking.id" class="crm-item">
          <div class="crm-item-header">
            <div class="crm-item-title">{{ booking.khachSan }}</div>
            <span
              :class="[
                'crm-badge',
                booking.action === 'checkout'
                  ? 'crm-badge--red'
                  : 'crm-badge--green',
              ]"
            >
              {{ booking.action }}
            </span>
          </div>
          <div class="crm-item-meta row-wrap">
            <span class="w-100 mb-1">
              <strong>{{ booking.maDatPhong }}</strong>
              <span v-if="booking.loaiPhong"
class="text-light ml-1"
                >· {{ booking.loaiPhong }}</span>
            </span>
            <span>{{
              $t('CRM_CONFIGS.BOOKING_HISTORY.CHECK_IN', {
                date: formatDate(booking.ngayDen),
              })
            }}</span>
            <span>{{
              $t('CRM_CONFIGS.BOOKING_HISTORY.CHECK_OUT', {
                date: formatDate(booking.ngayDi),
              })
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: {
    Spinner,
    Modal: () => import('../../../../components/Modal.vue'),
  },
  mixins: [alertMixin],
  props: {
    contactId: {
      type: [Number, String],
      required: true,
    },
    crmContact: {
      type: Object,
      required: true,
    },
    customAttributes: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeTab: 'bills',
      tabItems: [
        { id: 'bills', label: 'Đơn hàng' },
        { id: 'bookings', label: 'Lưu trú' },
        { id: 'notes', label: 'Ghi chú' },
        { id: 'products', label: 'Thống kê' },
        { id: 'vouchers', label: 'Voucher' },
      ],
      isLoading: true,
      hasError: false,
      bills: [],
      isLoadingBookings: true,
      hasBookingsError: false,
      bookings: [],
      isLoadingNotes: true,
      hasNotesError: false,
      notes: [],
      isLoadingProducts: true,
      hasProductsError: false,
      productStats: [],
      isLoadingVouchers: true,
      hasVouchersError: false,
      vouchers: [],
      showCreateNoteModal: false,
      isCreatingNote: false,
      newNote: { noteType: 0, message: '' },
      localCrmContact: null,
    };
  },
  computed: {
    loadingStates() {
      return {
        bills: this.isLoading,
        bookings: this.isLoadingBookings,
        notes: this.isLoadingNotes,
        products: this.isLoadingProducts,
        vouchers: this.isLoadingVouchers,
      };
    },
    errorStates() {
      return {
        bills: this.hasError,
        bookings: this.hasBookingsError,
        notes: this.hasNotesError,
        products: this.hasProductsError,
        vouchers: this.hasVouchersError,
      };
    },
    displayedCrmContact() {
      return this.localCrmContact || this.crmContact;
    },
    tabErrorKey() {
      const map = {
        bills: 'BILL_HISTORY',
        bookings: 'BOOKING_HISTORY',
        notes: 'NOTES',
        products: 'PRODUCTS',
        vouchers: 'VOUCHERS',
      };
      return map[this.activeTab] || 'BILL_HISTORY';
    },
    emptyMessage() {
      const map = {
        bills: this.$t('CRM_CONFIGS.BILL_HISTORY.NO_BILLS'),
        bookings: this.$t('CRM_CONFIGS.BOOKING_HISTORY.NO_BOOKINGS'),
        notes: this.$t('CRM_CONFIGS.NOTES.NO_NOTES'),
        products: this.$t('CRM_CONFIGS.PRODUCTS.NO_PRODUCTS'),
        vouchers: this.$t('CRM_CONFIGS.VOUCHERS.NO_VOUCHERS'),
      };
      return map[this.activeTab] || '';
    },
  },
  watch: {
    crmContact: {
      handler() {
        this.fetchGuestDetails();
        this.fetchBillHistory();
        this.fetchBookingHistory();
        this.fetchNotes();
        this.fetchProductStats();
        this.fetchVouchers();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchGuestDetails();
    this.fetchBillHistory();
    this.fetchBookingHistory();
    this.fetchNotes();
    this.fetchProductStats();
    this.fetchVouchers();
  },
  methods: {
    async fetchGuestDetails() {
      try {
        const identifier = this.crmContact.maKhach || this.crmContact.id;
        const responseData = await this.$store.dispatch(
          'contacts/getCrmGuest',
          identifier
        );
        if (responseData) {
          this.localCrmContact = responseData.data || responseData;
        }
      } catch {
        // silent fail
      }
    },
    isTabEmpty(tabId) {
      if (tabId === 'bills') return !this.bills.length;
      if (tabId === 'bookings') return !this.bookings.length;
      if (tabId === 'notes') return !this.notes.length;
      if (tabId === 'products') return !this.productStats.length;
      if (tabId === 'vouchers') return !this.vouchers.length;
      return true;
    },
    getTabIcon(tabId) {
      const icons = {
        bills: 'cart',
        bookings: 'home',
        notes: 'note-edit',
        products: 'data-usage',
        vouchers: 'ticket',
      };
      return icons[tabId] || 'folder';
    },
    getProductWeight(qty) {
      if (!this.productStats.length) return 0;
      const maxQty = Math.max(...this.productStats.map(p => p.quantity));
      return (qty / maxQty) * 100;
    },
    async fetchBillHistory() {
      this.isLoading = true;
      this.hasError = false;
      try {
        const responseData = await this.$store.dispatch(
          'contacts/getCrmBillHistory',
          this.crmContact.maKhach
        );
        this.bills = (responseData && responseData.data) || [];
      } catch {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchBookingHistory() {
      this.isLoadingBookings = true;
      this.hasBookingsError = false;
      try {
        const responseData = await this.$store.dispatch(
          'contacts/getCrmBookingHistory',
          this.crmContact.maKhach
        );
        this.bookings = (responseData && responseData.data) || [];
      } catch {
        this.hasBookingsError = true;
      } finally {
        this.isLoadingBookings = false;
      }
    },
    async fetchNotes() {
      this.isLoadingNotes = true;
      this.hasNotesError = false;
      try {
        const responseData = await this.$store.dispatch(
          'contacts/getCrmNotes',
          this.crmContact.maKhach
        );
        const noteList =
          (responseData && (responseData.data || responseData)) || [];
        this.notes = noteList.sort(
          (a, b) => new Date(b.ngayTao) - new Date(a.ngayTao)
        );
      } catch {
        this.hasNotesError = true;
      } finally {
        this.isLoadingNotes = false;
      }
    },
    async fetchProductStats() {
      this.isLoadingProducts = true;
      this.hasProductsError = false;
      try {
        const responseData = await this.$store.dispatch(
          'contacts/getCrmProductStats',
          this.crmContact.id
        );
        const products =
          (responseData && (responseData.data || responseData)) || [];
        if (products.length > 0) {
          const grouped = products.reduce((acc, curr) => {
            const { name, quantity } = this.parseProductInfo(curr.productName);
            if (!acc[name]) {
              acc[name] = {
                id: curr.productId,
                name,
                quantity: 0,
                totalPrice: 0,
                lastBought: curr.ngayTao,
              };
            }
            acc[name].quantity += quantity;
            acc[name].totalPrice += curr.productPrice * quantity;
            if (new Date(curr.ngayTao) > new Date(acc[name].lastBought))
              acc[name].lastBought = curr.ngayTao;
            return acc;
          }, {});
          this.productStats = Object.values(grouped).sort(
            (a, b) => b.quantity - a.quantity
          );
        }
      } catch {
        this.hasProductsError = true;
      } finally {
        this.isLoadingProducts = false;
      }
    },
    async fetchVouchers() {
      this.isLoadingVouchers = true;
      this.hasVouchersError = false;
      try {
        const responseData = await this.$store.dispatch(
          'contacts/getCrmVouchers',
          this.crmContact.dienThoai
        );
        this.vouchers =
          (responseData && (responseData.data || responseData)) || [];
      } catch {
        this.hasVouchersError = true;
      } finally {
        this.isLoadingVouchers = false;
      }
    },
    async unlinkContact() {
      if (!confirm(this.$t('CRM_CONFIGS.BILL_HISTORY.UNLINK_CONFIRM'))) return;
      try {
        await this.$store.dispatch('contacts/deleteCustomAttributes', {
          id: this.contactId,
          customAttributes: ['crm_contact'],
        });
        this.showAlert(this.$t('CRM_CONFIGS.BILL_HISTORY.UNLINK_SUCCESS'));
      } catch {
        this.showAlert(this.$t('CRM_CONFIGS.BILL_HISTORY.UNLINK_ERROR'));
      }
    },
    formatDate(dateString, compact = false) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (compact)
        return date.toLocaleDateString('vi-VN', {
          day: '2-digit',
          month: '2-digit',
        });
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatCurrency(amount) {
      if (!amount) return '0đ';
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        minimumFractionDigits: 0,
      }).format(amount);
    },
    formatVoucherValue(item) {
      if (item.discountType === 1) return `-${item.amount}%`;
      if (item.discountType === 2)
        return `-${this.formatCurrency(item.amount)}`;
      if (item.discountType === 3) return 'Gift';
      return '';
    },
    parseProductInfo(productName) {
      if (!productName) return { name: '', quantity: 1 };
      const match = productName.match(/^(.*?)(?:\{\{(\d+)\}\})?$/);
      return {
        name: ((match && match[1]) || productName).trim(),
        quantity: (match && match[2] && parseInt(match[2], 10)) || 1,
      };
    },
    formatBillProductName(productName) {
      const { name, quantity } = this.parseProductInfo(productName);
      if (quantity > 1) return `${name} x${quantity}`;
      return name;
    },
    noteTypeString(type) {
      const keys = ['INTERNAL', 'POS', 'PMS', 'POS_INTERNAL'];
      return this.$t(`CRM_CONFIGS.NOTES.TYPE_${keys[type] || 'UNKNOWN'}`);
    },
    noteTypeColor(type) {
      const colors = [
        'crm-badge--blue',
        'crm-badge--purple',
        'crm-badge--teal',
        'crm-badge--orange',
      ];
      return colors[type] || 'crm-badge--default';
    },
    openNoteModal() {
      this.showCreateNoteModal = true;
      this.newNote = { noteType: 0, message: '' };
      this.$nextTick(() => {
        if (this.$refs.noteTextArea) {
          this.$refs.noteTextArea.focus();
        }
      });
    },
    closeNoteModal() {
      this.showCreateNoteModal = false;
    },
    async submitCreateNote() {
      if (!this.newNote.message.trim()) return;
      this.isCreatingNote = true;
      try {
        await this.$store.dispatch('contacts/createCrmNote', {
          ...this.newNote,
          maKhach: this.crmContact.maKhach,
        });
        this.showAlert(this.$t('CRM_CONFIGS.NOTES.CREATE_SUCCESS'));
        this.closeNoteModal();
        await this.fetchNotes();
      } catch {
        this.showAlert(this.$t('CRM_CONFIGS.NOTES.CREATE_ERROR'));
      } finally {
        this.isCreatingNote = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.contact-crm-info {
  padding: var(--space-small) 0;
  @apply border-t border-slate-100 dark:border-slate-800/50;
  margin-top: var(--space-small);
}

/* ── Header ─────────────────────────────────────── */
.crm-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-smaller);
  padding: 0 var(--space-small) var(--space-small);
}

.crm-section-title {
  display: flex;
  align-items: center;
  gap: var(--space-micro);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  @apply text-slate-700 dark:text-slate-300;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.crm-section-actions {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  flex-wrap: wrap;
  justify-content: flex-end;

  ::v-deep .button {
    white-space: nowrap;
  }
}

/* ── Contact Info Card ─────────────────────────── */
.crm-contact-card {
  @apply bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800/50;
  border-radius: var(--border-radius-medium);
  margin: 0 var(--space-small) var(--space-normal);
  overflow: hidden;
}

.crm-contact-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-micro) var(--space-small);
  gap: var(--space-small);
  font-size: var(--font-size-mini);
  @apply border-b border-slate-50 dark:border-slate-800/50;

  &:last-child {
    border-bottom: none;
  }
}

.crm-label {
  @apply text-slate-500 dark:text-slate-400;
  flex-shrink: 0;
}

.crm-value {
  font-weight: var(--font-weight-medium);
  @apply text-slate-800 dark:text-slate-100;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.mono {
    font-family: monospace;
    @apply text-woot-500 dark:text-woot-400;
  }
}

/* ── Tabs ─────────────────────────────────────── */
.crm-tabs {
  display: flex;
  @apply border-b border-slate-100 dark:border-slate-800/50;
  margin-bottom: var(--space-small);
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
}

.crm-tab {
  padding: var(--space-smaller) var(--space-small);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  @apply text-slate-500 dark:text-slate-400;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition:
    color 0.15s,
    border-color 0.15s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    @apply text-woot-500;
  }

  &--active {
    @apply text-woot-500 border-woot-500 font-bold;
  }
}

/* ── Tab Content ─────────────────────────────── */
.crm-tab-content {
  max-height: 480px;
  overflow-y: auto;
  padding: 0 var(--space-small);

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    @apply bg-slate-100 dark:bg-slate-800;
    border-radius: 10px;
  }
}

/* ── State: loading / error / empty ─────────── */
.crm-state-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-large) 0;
}

.crm-state-text {
  font-size: var(--font-size-mini);
  @apply text-slate-500 dark:text-slate-400;
  text-align: center;
}

.crm-empty-icon {
  @apply text-slate-200 dark:text-slate-700;
}

/* ── List & Items ─────────────────────────── */
.crm-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  padding-bottom: var(--space-large);
}

.crm-item {
  padding: var(--space-small);
  @apply border border-slate-100 dark:border-slate-800/50 bg-white dark:bg-slate-900;
  border-radius: var(--border-radius-medium);
}

.crm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-small);
  margin-bottom: var(--space-micro);
}

.crm-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-small);
  margin-bottom: var(--space-micro);
}

.crm-item-title {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  @apply text-slate-800 dark:text-slate-100;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crm-item-amount {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  @apply text-woot-500;
  white-space: nowrap;
  flex-shrink: 0;
}

.crm-item-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  @apply text-slate-500 dark:text-slate-400;
  margin-top: var(--space-micro);
}

.crm-item-date {
  font-size: 11px;
  @apply text-slate-500 dark:text-slate-400;
}

/* ── Bill Products ────────────────────────── */
.crm-item-products {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: var(--space-micro);
}

.crm-product-chip {
  font-size: 10px;
  @apply bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300;
  border-radius: var(--border-radius-small);
  padding: 2px 6px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crm-product-more {
  font-size: 10px;
  @apply text-woot-500;
  padding: 2px 4px;
}

/* ── Notes ────────────────────────────────── */
.crm-notes-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: var(--space-micro);
}

.crm-note-body {
  font-size: var(--font-size-mini);
  @apply text-slate-800 dark:text-slate-200;
  margin: var(--space-micro) 0;
  line-height: 1.5;
}

.crm-note-author {
  font-size: 10px;
  @apply text-slate-400 dark:text-slate-500;
}

/* ── Badge ────────────────────────────────── */
.crm-badge {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  padding: 2px 6px;
  border-radius: var(--border-radius-small);
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &--blue {
    background: #eff6ff;
    color: #3b82f6;
  }
  &--purple {
    background: #faf5ff;
    color: #9333ea;
  }
  &--teal {
    background: #f0fdfa;
    color: #0d9488;
  }
  &--orange {
    background: #fff7ed;
    color: #ea580c;
  }
  &--green {
    background: #f0fdf4;
    color: #16a34a;
  }
  &--red {
    background: #fef2f2;
    color: #dc2626;
  }
  &--default {
    background: #f8fafc;
    color: #64748b;
  }
}

/* ── Voucher ─────────────────────────────── */
.crm-voucher-code {
  font-family: monospace;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  @apply text-slate-800 dark:text-slate-100 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-800/50;
  border-radius: var(--border-radius-small);
  padding: 2px 6px;
}

/* ── Products/Stats ───────────────────────── */
.crm-product-stat {
  padding-bottom: var(--space-small);
  @apply border-b border-slate-50 dark:border-slate-800/50;

  &:last-child {
    border-bottom: none;
  }
}

.crm-product-stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-micro);
}

.crm-product-name {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  @apply text-slate-800 dark:text-slate-100;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  padding-right: var(--space-small);
}

.crm-product-qty {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  @apply text-slate-500 dark:text-slate-400;
}

.crm-progress-bar {
  height: 4px;
  @apply bg-slate-50 dark:bg-slate-800;
  border-radius: var(--border-radius-small);
  overflow: hidden;
  margin-bottom: var(--space-micro);
}

.crm-progress-fill {
  height: 100%;
  @apply bg-woot-500;
  border-radius: var(--border-radius-small);
  transition: width 0.6s ease;
}

.crm-product-footer {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  @apply text-slate-500 dark:text-slate-400;
}

/* ── Modal Footer ───────────────────────── */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-normal);
}
</style>
